/**
 * Owner Promotions — banner card grid + CRUD.
 *
 * Phase W-2 additions to this editor:
 *   - drawer form gains `validity_start` (optional date), `long_description`
 *     (textarea), `terms_conditions` (textarea), and `branch_ids` (checkbox
 *     grid over the brand's branches).
 *   - banner cards show a "Limited to N branches" chip when restricted,
 *     and an "Expired" chip when `valid_until` is in the past (owner
 *     surface — backend `is_expired` is only on the public response so
 *     we derive it client-side here).
 *   - DELETE is now soft on the backend; the row disappears from the
 *     owner list because the owner list endpoint excludes `deleted_at`
 *     rows. No additional UI is needed here for that milestone.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Megaphone, Edit, Power, Trash2, MapPin, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { formatDate } from '@/lib/utils';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, EmptyState, StatusPill,
} from '@/components/ui_primitives/Misc';
import Button from '@/components/ui_primitives/Button';
import Drawer from '@/components/ui_primitives/Drawer';
import { FieldRow, Input, Textarea } from '@/components/ui_primitives/Field';
import ImageUploader from '@/components/ui_primitives/ImageUploader';
import { confirm } from '@/components/ui_primitives/confirm';

// W-2 — schema gains 4 optional fields. Validity-range guard runs as a
// `.refine` so the error binds to `validity_start` for the form UI;
// backend re-validates with the same rule.
const schema = z
  .object({
    title: z.string().min(1).max(120),
    description: z.string().min(1).max(500),
    valid_until: z.string().min(1, 'Required'),
    is_active: z.boolean().default(true),
    validity_start: z.string().optional().default(''),
    long_description: z.string().max(5000).optional().default(''),
    terms_conditions: z.string().max(5000).optional().default(''),
  })
  .refine(
    (v) => !v.validity_start || !v.valid_until || v.validity_start <= v.valid_until,
    { path: ['validity_start'], message: 'Start date must be on or before end date' },
  );

export default function OwnerPromotionsPage() {
  const token = useAuthStore((s) => s.token);
  const brandId = useAuthStore((s) => s.user?.brand_id);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const promosQ = useQuery({
    queryKey: qk.ownerPromotions(brandId),
    queryFn: () => api.ownerPromotions(token),
    enabled: !!token && !!brandId,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: qk.ownerPromotions(brandId) });
  const toggle = useMutation({
    mutationFn: ({ id, on }) => api.ownerPatchPromotion(token, id, { is_active: on }),
    onSuccess: () => { toast.success('Saved'); invalidate(); },
    onError: (e) => toast.error(String(e?.detail ?? 'Action failed')),
  });
  const remove = useMutation({
    mutationFn: (id) => api.ownerDeletePromotion(token, id),
    onSuccess: () => { toast.success('Promotion deleted'); invalidate(); },
    onError: (e) => toast.error(String(e?.detail ?? 'Delete failed')),
  });

  if (promosQ.isLoading) return <FullSpinner />;
  if (promosQ.error) return <QueryError error={promosQ.error} onRetry={() => promosQ.refetch()} />;
  const items = promosQ.data || [];
  const nowIso = new Date().toISOString();

  return (
    <div className="space-y-6" data-testid="owner-promotions-page">
      <PageHeader
        kicker="Promotions"
        title="Marketing banners"
        subtitle="Promote limited-time offers and events. Customers see active promotions on home."
        actions={<Button leftIcon={<Plus size={14} />} onClick={() => setEditing('new')} testID="promo-add">Add promotion</Button>}
      />
      {items.length === 0 ? (
        <EmptyState
          icon={<Megaphone size={20} />}
          title="No promotions yet"
          subtitle="Create your first promotion banner to drive visits and engagement."
          action={<Button leftIcon={<Plus size={14} />} onClick={() => setEditing('new')}>Create promotion</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {items.map((p) => {
            const expired = (p.valid_until ?? '') < nowIso;
            const limited = Array.isArray(p.branch_ids) && p.branch_ids.length > 0;
            return (
              <div
                key={p.id}
                className="bg-[#1A1A1A] border border-white/8 rounded-2xl overflow-hidden flex flex-col"
                data-testid={`promo-card-${p.id}`}
              >
                <div className="relative aspect-[3/1] bg-black/40">
                  {p.image_url ? (
                    <img src={resolveImageUrl(p.image_url)} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
                  ) : null}
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    {p.is_active ? <StatusPill variant="gold">Active</StatusPill> : <StatusPill variant="slate">Inactive</StatusPill>}
                    {expired ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-400/55 text-[10px] font-mono uppercase tracking-wider text-rose-300"
                        data-testid={`promo-card-${p.id}-expired-chip`}
                      >
                        <Clock size={10} /> Expired
                      </span>
                    ) : null}
                  </div>
                  {limited ? (
                    <div
                      className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/55 border border-white/15 backdrop-blur text-[10px] font-mono uppercase tracking-wider text-white/85"
                      data-testid={`promo-card-${p.id}-limited-chip`}
                    >
                      <MapPin size={10} /> Limited to {p.branch_ids.length} branch{p.branch_ids.length === 1 ? '' : 'es'}
                    </div>
                  ) : null}
                </div>
                <div className="p-4 flex-1 flex flex-col gap-2">
                  <div className="font-display font-semibold text-base text-white truncate">{p.title}</div>
                  <p className="text-xs text-white/55 line-clamp-2">{p.description}</p>
                  <div className="text-[11px] text-white/35">Valid until {formatDate(p.valid_until)}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <Button size="sm" variant="subtle" leftIcon={<Edit size={12} />} onClick={() => setEditing(p)} testID={`promo-edit-${p.id}`}>Edit</Button>
                    <Button size="sm" variant="ghost" leftIcon={<Power size={12} />} onClick={() => toggle.mutate({ id: p.id, on: !p.is_active })} testID={`promo-toggle-${p.id}`}>
                      {p.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button size="sm" variant="danger" leftIcon={<Trash2 size={12} />} onClick={async () => {
                      const ok = await confirm({ title: `Delete "${p.title}"?`, message: 'The promotion will be removed from this list and from all customer surfaces.', confirmText: 'Delete', tone: 'danger' });
                      if (ok) remove.mutate(p.id);
                    }} testID={`promo-delete-${p.id}`}>Delete</Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing ? (
        <PromoForm
          promo={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { invalidate(); setEditing(null); }}
        />
      ) : null}
    </div>
  );
}

function PromoForm({ promo, onClose, onSaved }) {
  const token = useAuthStore((s) => s.token);
  const isEdit = !!promo;
  const [image, setImage] = useState(promo?.image_url ?? '');
  const [branchIds, setBranchIds] = useState(promo?.branch_ids ?? []);

  // W-2 — load the owner's branches for the checkbox grid. Same query
  // key the BranchesPage uses, so the cache is shared.
  const branchesQ = useQuery({
    queryKey: qk.ownerBranches('me'),
    queryFn: () => api.ownerBranches(token),
    enabled: !!token,
  });
  const branches = branchesQ.data ?? [];

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      title: promo?.title ?? '',
      description: promo?.description ?? '',
      valid_until: promo?.valid_until ? promo.valid_until.slice(0, 10) : '',
      is_active: promo?.is_active ?? true,
      validity_start: promo?.validity_start ? promo.validity_start.slice(0, 10) : '',
      long_description: promo?.long_description ?? '',
      terms_conditions: promo?.terms_conditions ?? '',
    },
  });

  const onSubmit = async (vals) => {
    if (!image) { toast.error('Banner image is required'); return; }
    try {
      // Send empty strings (not null) for the optional copy fields so
      // round-tripping a previously-set value to empty actually clears it.
      const body = {
        ...vals,
        image_url: image,
        long_description: vals.long_description ?? '',
        terms_conditions: vals.terms_conditions ?? '',
        validity_start: vals.validity_start || null,
        branch_ids: branchIds,
      };
      if (isEdit) await api.ownerPatchPromotion(token, promo.id, body);
      else await api.ownerCreatePromotion(token, body);
      toast.success(isEdit ? 'Promotion updated' : 'Promotion created');
      onSaved();
    } catch (e) {
      // Surface backend validation errors verbatim when present (e.g.
      // invalid_validity_range, invalid_branch_id).
      const detail = e?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message ?? 'Save failed');
      toast.error(msg);
    }
  };

  const toggleBranch = (id) => {
    setBranchIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  return (
    <Drawer open onClose={onClose}
      title={isEdit ? 'Edit promotion' : 'New promotion'}
      subtitle="Banners appear on customer home pages."
      testID="promo-drawer"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit(onSubmit)} loading={isSubmitting} testID="promo-save">{isEdit ? 'Save changes' : 'Create'}</Button>
        </>
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <FieldRow label="Banner image" required>
          <ImageUploader value={image} onChange={setImage} aspect="banner" />
        </FieldRow>
        <FieldRow label="Title" required error={errors.title?.message}>
          <Input {...register('title')} />
        </FieldRow>
        <FieldRow label="Short description" required error={errors.description?.message}
          hint="One-line summary shown on home and brand pages.">
          <Textarea rows={3} {...register('description')} />
        </FieldRow>

        {/* W-2 — validity range */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldRow label="Valid from (optional)" error={errors.validity_start?.message}
            hint="Leave empty to start immediately">
            <Input type="date" {...register('validity_start')} data-testid="promo-form-validity-start" />
          </FieldRow>
          <FieldRow label="Valid until *" required error={errors.valid_until?.message}>
            <Input type="date" {...register('valid_until')} data-testid="promo-form-valid-until" />
          </FieldRow>
        </div>

        {/* W-2 — long description */}
        <FieldRow label="Detailed description (optional)"
          hint="Shown on the promotion detail page. Line breaks are preserved.">
          <Textarea rows={6} {...register('long_description')} data-testid="promo-form-long-description" />
        </FieldRow>

        {/* W-2 — terms & conditions */}
        <FieldRow label="Terms & conditions (optional)"
          hint="Eligibility, redemption rules, etc.">
          <Textarea rows={4} {...register('terms_conditions')} data-testid="promo-form-terms" />
        </FieldRow>

        {/* W-2 — branch availability */}
        <FieldRow label="Available at branches"
          hint="Leave all unchecked = available at all branches.">
          {branchesQ.isLoading ? (
            <div className="h-16 rounded-xl bg-white/[0.03] border border-white/5 animate-pulse" />
          ) : branches.length === 0 ? (
            <div className="text-xs text-white/45">No branches yet — add one in Locations first.</div>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-2"
              data-testid="promo-form-branches"
            >
              {branches.map((b) => {
                const on = branchIds.includes(b.id);
                return (
                  <label
                    key={b.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-colors text-sm ${
                      on
                        ? 'bg-gold/10 border-gold/45 text-white'
                        : 'bg-[#1A1A1A] border-white/8 text-white/75 hover:border-white/15'
                    }`}
                    data-testid={`promo-form-branch-${b.id}`}
                  >
                    <input
                      type="checkbox"
                      className="accent-gold w-4 h-4"
                      checked={on}
                      onChange={() => toggleBranch(b.id)}
                    />
                    <span className="truncate">{b.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </FieldRow>

        <FieldRow label="Status" hint="Customers see active promotions only">
          <label className="inline-flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" className="accent-gold w-4 h-4" {...register('is_active')} /> Active
          </label>
        </FieldRow>
      </form>
    </Drawer>
  );
}
